import { ADDONS, ADDON_MODULES, MODULES, isAddonModule, type ModuleKey } from "./modules";
import { feature, type FeatureDefinition } from "./feature-definition";
import { PHOTO_READER } from "./receiving-photo-reader";

export { ADDONS, ADDON_MODULES, MODULES, isAddonModule, type ModuleKey };

export type { FeatureDefinition };

/** The feature registry. Never rename a key: keys are stored in `modules.feature_keys` and per-user
 * menu preferences. */
export const FEATURES = {
  dashboard: feature("Dashboard", "core", { alwaysOn: true }),
  settings: feature("Settings", "core", { alwaysOn: true }),
  mentions: feature("Mentions", "core", { alwaysOn: true }),
  /** Staff messaging: direct messages, groups, channels. Comms, its own workspace and module: on
   * through its switch (every organization made before 2 Nov 2026 has it), a trial or free
   * months. */
  messaging: feature("Messages", "comms"),
  /** The meeting calendar; a meeting's chat and call are messaging's. */
  meetings: feature("Meetings", "comms", { follows: "messaging" }),
  /** Video in conversations and meetings. A platform override switches it off where an operator
   * may not record faces on a call; nothing works without the LiveKit service either. */
  "video-calls": feature("Video calls", "comms", { follows: "messaging" }),
  /** The family community: feed, notes from family, events. On wherever residents are. */
  community: feature("Community", "core", { follows: "residents" }),
  /** Families booking video visits with a resident; the room is LiveKit's, like staff calls. */
  "family-visits": feature("Family video visits", "core", { follows: "community" }),
  menu: feature("Menu", "core", { alwaysOn: true }),
  "plans-modules": feature("Plans & modules", "admin-core", { alwaysOn: true }),

  residents: feature("Residents", "core"),
  medications: feature("Medications", "core"),
  "med-pass": feature("Med pass", "core"),
  "med-pass-report": feature("Med pass report", "core"),
  /** The care pass: laundry, room cleaning, bathing and supervision scheduled on the building's own
   * shifts. Core's, apart from Olivia's rota, so a building without her still runs it. */
  "care-pass": feature("Care pass", "core"),
  "pharmacy-receiving": feature("Pharmacy receiving", "core"),
  "receiving-photo-reader": feature("Delivery reader (AI)", PHOTO_READER.module, {
    follows: PHOTO_READER.follows,
    description:
      "Reads a pharmacy's packing slip, or a photo of a medication package or receipt, into Receiving. The file goes to an AI model that keeps nothing, and is never stored.",
  }),
  "supply-forecast": feature("Supply forecast", "core"),
  activities: feature("Activities", "core"),
  /** The building's programme for residents, on wherever the care log is. */
  "activity-calendar": feature("Activity calendar", "core", {
    follows: "activities",
  }),
  incidents: feature("Incidents", "core"),
  /** The staff rota: Olivia's, as the site sells her. Resident appointments are `transport`. */
  schedule: feature("Schedule", "hr"),
  census: feature("Census", "core"),
  "daily-roll": feature("Daily roll", "core"),
  providers: feature("Provider directory", "core"),
  institutions: feature("Institutions directory", "core"),
  pharmacies: feature("Pharmacy directory", "core"),
  "pcp-visits": feature("PCP visit report", "core"),
  "notes-center": feature("Notes center", "core"),
  "task-center": feature("Task center", "core"),
  transport: feature("Appointments", "core"),
  "care-plan-signature-queue": feature("Care plan signature queue", "core"),
  "care-plans": feature("Care plans", "core"),
  "move-in": feature("Move-in", "core"),
  admissions: feature("Admissions", "core"),
  // Part of keeping charts, so on wherever residents are: a building cannot
  // choose not to be surveyed.
  "survey-access": feature("Survey access", "core", { follows: "residents" }),
  /* On for every building: a sign-in sheet is not something a plan sells, and the
     evacuation list cannot depend on one. The kiosk is Grace's, so it needs her. */
  visitors: feature("Visitors", "visitors", { alwaysOn: true }),
  "visitor-kiosk": feature("Visitor kiosk", "visitors"),
  /* Grace's voice, recognition and memory at the kiosk. Wherever the kiosk is, so a
     platform override can switch her off; each building still turns her on itself. */
  grace: feature("Grace voice agent", "visitors", { follows: "visitor-kiosk" }),
  /* Residents, staff and returning visitors recognised at the kiosk. Wherever the kiosk is; each
     building still switches it on, and nothing is enrolled until it does. */
  "face-recognition": feature("Face recognition", "visitors", {
    follows: "visitor-kiosk",
  }),

  "user-management": feature("Employees", "admin-core"),
  "role-management": feature("Roles & permissions", "admin-core"),
  /* The organization's own subscription to PEOPLE AI Workforce; the owner's permission gates it. */
  billing: feature("Billing", "admin-core", { alwaysOn: true }),
  facilities: feature("Facilities", "admin-core"),
  "facility-profile": feature("Facility profile", "admin-core", { follows: "facilities" }),
  tags: feature("Tags", "admin-core"),
  checklists: feature("Checklists", "admin-core"),
  "assistive-devices": feature("Assistive devices", "admin-core"),
  "document-types": feature("Document types", "admin-core"),
  "api-keys": feature("API keys", "admin-core", { alwaysOn: true }),
  // Settings screens for lists the building maintains itself: always on, and
  // the permission on the navigation row decides who may open one.
  "incident-categories": feature("Incident categories", "admin-core", {
    alwaysOn: true,
  }),
  // The admissions configuration screen in the settings hub: always on, like
  // the other hub screens, and no seeded plan carries its key.
  "admission-forms": feature("Move-in packets", "admin-core", {
    alwaysOn: true,
  }),
  /** Asking the platform team to switch something on is never sellable, so it is always on. */
  activation: feature("Activation requests", "admin-core", { alwaysOn: true }),
  // Always on, not sellable: `platformOnly` on its navigation row gates it, and a platform admin
  // has no plan to consult.
  "platform-console": feature("Platform console", "admin-core", {
    alwaysOn: true,
  }),
  /* People AI's own Sarah and Shelby: gated by `platformOnly` like the console they came out of. */
  "platform-calls": feature("People AI calls", "admin-core", {
    alwaysOn: true,
  }),
  "platform-conversations": feature("People AI conversations", "admin-core", {
    alwaysOn: true,
  }),
  "platform-leads": feature("People AI leads", "admin-core", {
    alwaysOn: true,
  }),
  "platform-lines": feature("People AI lines", "admin-core", {
    alwaysOn: true,
  }),
  "platform-desk": feature("People AI desk", "admin-core", { alwaysOn: true }),
  "platform-aria-feedback": feature("Aria feedback", "admin-core", { alwaysOn: true }),
  "platform-sales-assistant": feature("People AI sales assistant", "admin-core", {
    alwaysOn: true,
  }),
  "support-desk": feature("Support desk", "admin-core", { alwaysOn: true }),
  "platform-form-requests": feature("Form requests", "admin-core", { alwaysOn: true }),
  "platform-team": feature("Platform team", "admin-core", { alwaysOn: true }),
  "platform-roles": feature("Native roles", "admin-core", { alwaysOn: true }),
  "platform-activity": feature("Platform activity", "admin-core", { alwaysOn: true }),
  "platform-settings": feature("Platform settings", "admin-core", { alwaysOn: true }),
  "demographic-lists": feature("Resident lists", "admin-core", {
    alwaysOn: true,
  }),
  /** Organization-wide settings, always on: the Medicaid switch it carries gates the Medicaid
   * module, so a plan check here would be circular. */
  organization: feature("Organization", "admin-core", { alwaysOn: true }),

  "document-templates": feature("Document templates", "documents"),

  crm: feature("CRM & marketing", "crm"),
  leads: feature("Leads", "crm"),
  tours: feature("Tours", "crm"),
  campaigns: feature("Campaigns", "crm"),
  "call-logs": feature("Call logs", "crm"),
  /** Part of the CRM rather than sold beside it: on wherever the CRM is. */
  prospecting: feature("Prospecting", "crm", { follows: "crm" }),
  "referral-sources": feature("Referral sources", "crm", { follows: "crm" }),
  fax: feature("Fax", "communications"),
  /** One page for everyone a building writes to, replacing three Core directory screens, so it is
   * Core's; the lead half it reads still requires `crm`. */
  directory: feature("Directory", "core"),
  // Settings screens that configure a product: on wherever the product is,
  // never always on, or a plan without the product shows them.
  "fax-settings": feature("Fax settings", "communications", { follows: "fax" }),
  "crm-lead-fields": feature("Lead fields", "crm", { follows: "crm" }),
  "crm-events": feature("Events", "crm", { follows: "crm" }),
  "crm-email-templates": feature("Email templates", "crm", { follows: "crm" }),
  "crm-website-lead-sources": feature("Website lead sources", "crm", {
    follows: "crm",
  }),

  "rent-roll": feature("Rent roll", "financials"),
  "ar-summary": feature("AR summary", "financials"),
  "monthly-responsibility": feature("Monthly responsibility", "financials"),
  "bulk-operations": feature("Bulk operations", "financials"),
  "invoice-roll": feature("Invoice roll", "financials"),
  "expense-entries": feature("Expense entries", "financials"),
  vendors: feature("Vendors", "financials"),
  "pnl-report": feature("P&L report", "financials"),
  "ap-report": feature("AP report", "financials"),
  "trust-fund-report": feature("Trust fund report", "financials"),
  "expense-categories": feature("Expense categories", "financials"),
  "qb-mappings": feature("QuickBooks mappings", "financials"),
  "transaction-tags": feature("Transaction tags", "financials"),
  "online-payments": feature("Online payments", "financials"),
  charges: feature("Charges", "financials"),
  "payments-received": feature("Payments received", "financials"),
  collections: feature("Collections", "financials"),

  "therapy-caseload": feature("Therapy caseload", "therapies"),
  "therapy-cosign-queue": feature("Therapy co-sign queue", "therapies"),
  "therapy-payroll": feature("Therapy payroll", "therapies"),
  "therapy-cpt-codes": feature("Therapy CPT codes", "therapies"),
  "therapy-note-templates": feature("Therapy note templates", "therapies"),
  "therapy-vendors": feature("Therapy vendors", "therapies"),

  "medicaid-rates": feature("Medicaid rates", "medicaid", {
    medicaidOnly: true,
  }),
  "expiring-auths": feature("Expiring authorizations", "medicaid", {
    medicaidOnly: true,
  }),

  hiring: feature("Hiring", "hiring"),
  // The settings screens that configure hiring, on wherever hiring is (see the
  // CRM ones above). Every procedure behind them is `requireFeature("hiring")`.
  "ats-message-templates": feature("Hiring message templates", "hiring", {
    follows: "hiring",
  }),
  "ats-offer-templates": feature("Hiring offer templates", "hiring", {
    follows: "hiring",
  }),
  hr: feature("HR", "hr"),
  training: feature("Training", "training"),
  policies: feature("Policies", "policies"),
  /** Michael's library. Separate keys keep the library from putting the roll-up in anyone's
   * sidebar. */
  knowledgebase: feature("Knowledgebase", "policies"),
  "knowledgebase-rollup": feature("Knowledgebase roll-up", "policies"),

  "sarah-calls": feature("Front desk call log", "front-desk"),
  "sarah-messages": feature("Front desk text messages", "front-desk"),
  "sarah-analytics": feature("Front desk analytics", "front-desk"),
  "sarah-staff": feature("Front desk roster", "front-desk"),
  "sarah-agent": feature("Front desk agent", "front-desk"),
  "sarah-agent-test": feature("Front desk agent test", "front-desk"),
  "sarah-suppression": feature("Front desk suppression", "front-desk"),
  "sarah-escalation": feature("Front desk escalation", "front-desk"),
  "sarah-optimizer": feature("Front desk optimizer", "front-desk"),
  "sarah-org-settings": feature("Front desk credentials", "front-desk"),
  /* The organization's number bank (PEO-106): every line it owns and who holds each. Core's, since
     Nancy and Shelby text from a line too; buying one stays People AI's (`platform:manage`). */
  "phone-number-bank": feature("Phone numbers", "communications"),
  "sarah-facility-settings": feature("Front desk facility settings", "front-desk"),
  /* A transfer rings the person's open browser AND their mobile at once, and the first to press 1
     takes the call. Off unless an override switches it on: both at once on one iPhone sent
     transfers to voicemail. Otherwise the browser rings first, then the mobile. */
  "sarah-ring-both": feature("Ring browser and mobile together", "front-desk", {
    description:
      "A Sarah transfer rings the person's open browser phone and their mobile together; the first to accept takes the call.",
    optIn: true,
  }),
  /* The shared screen at the front desk. Its own key because it is bought on its own: a community
   * can run the call log without ever putting a tablet on the wall, and a device token resolves
   * this gate through the building's operator rather than through a signed-in person. */
  "sarah-board": feature("Front desk shift board", "front-desk"),

  /* Sales assistant beside Sarah on the same texting line. Own module, sold separately from the
   * front desk. One key per screen so a building can share conversations without the switches. */
  "shelby-conversations": feature("Sales assistant conversations", "sales-assistant"),
  "shelby-agent": feature("Sales assistant agent", "sales-assistant"),
  "shelby-settings": feature("Sales assistant settings", "sales-assistant"),

  "ai-assistant": feature("AI assistant (Aria)", "ai", {
    description: "The in-app copilot. Off by default; enable per organization.",
  }),
  "aria-files": feature("Aria file creation", "ai", {
    description:
      "Aria can create PDF, Word, Excel, CSV and Markdown files. Every file is audited and kept 30 days.",
    follows: "ai-assistant",
  }),
  "aria-auto-mode": feature("Aria automatic mode", "ai", {
    description: "Aria may make the changes a person asks for without a card each time.",
    follows: "ai-assistant",
  }),
  "aria-follow-ups": feature("Aria follow-ups", "ai", {
    description: "Aria checks back on things later and tells the person what she found.",
    follows: "ai-assistant",
  }),
  "aria-schedules": feature("Aria schedules", "ai", {
    description:
      "Aria runs a task a person wrote at the times they chose, and tells them what she found.",
    follows: "ai-assistant",
  }),
  /* Max joins a video call only when a host adds him, and hears it through the organization's own
     xAI account (Sarah's), so a community without one cannot add him. */
  "meeting-assistant": feature("Max, the meeting assistant", "ai", {
    description:
      "A host can add Max to a video call. He answers out loud when someone says his name and keeps nothing after the call.",
    follows: "ai-assistant",
  }),
  /* Amy runs one move-in from Start move-in until it closes. An AI employee of her own, sold and
     tried apart from Aria; her screens also need the move-in board itself ("move-in"). */
  "move-in-agent": feature("Amy, the move-in agent", "move-ins", {
    description:
      "Amy keeps each move-in from the lead to the chart, and closes it when the required steps are done.",
  }),
  /* Olivia, the workforce agent, in her own conversation; each of her tools still needs its own
     feature and permission. */
  "workforce-agent": feature("Olivia, the workforce agent", "hiring", {
    description:
      "Olivia answers hiring, training, HR and rota questions and prepares the changes, each confirmed by the person.",
  }),
  "aria-staff-activity": feature("Aria staff activity", "ai", {
    description:
      "Aria can say what a colleague changed recently, from the audit trail, to people who can read the staff roster. Off unless switched on.",
    optIn: true,
  }),
  "aria-web-search": feature("Aria web search", "ai", {
    description:
      "Aria may search the public web for drug facts and regulations. A query naming a resident is refused before it is sent. Off unless switched on.",
    optIn: true,
  }),
  // Nancy, the purchasing agent: she shops a building's list at the stores it
  // buys from. Three keys in her own add-on module: her orders, what she knows (a section of
  // her settings, still its own key so a plan can leave it out) and her settings.
  nancy: feature("Nancy", "nancy"),
  "nancy-graph": feature("What Nancy knows", "nancy"),
  "nancy-settings": feature("Nancy settings", "nancy"),
  // Her orders in the extension's side panel; its own key so one organization can be switched off.
  "nancy-panel": feature("Nancy in Chrome", "nancy", { follows: "nancy" }),
  // Menus: a kitchen's menu calendar, dishes, pantry and shopping list; the Kitchen agent works on
  // them in its own chat. Nancy's, in her workspace.
  "menu-planner": feature("Menus", "nancy"),
  "menu-agent": feature("Kitchen agent", "nancy"),
  "menu-web-search": feature("Kitchen agent web search", "nancy", {
    follows: "menu-agent",
    description:
      "The Kitchen agent may search the public web for recipes, serving sizes and what a product is while it writes dishes. Only the search words leave the building, and a search naming a resident is refused.",
  }),
  // Supplies: a building's supply closet, its calendar, its running supplies list and the products
  // to buy, kept by the Kitchen agent beside the menus. Nancy's, as Menus is.
  supplies: feature("Supplies", "nancy"),
  "supplies-agent": feature("Kitchen agent for Supplies", "nancy", {
    follows: "supplies",
    description:
      "The Kitchen agent keeps the supply closet, its calendar and the supplies list beside the menus, and Nancy reads the supplies list from it when she shops.",
  }),
  // David, the maintenance coordinator, in his own add-on module: the work orders, the parts shop
  // with what to buy, his chat, and his searches of the web for a fix or a part.
  maintenance: feature("Work orders", "maintenance"),
  "maintenance-shop": feature("Parts shop", "maintenance", { follows: "maintenance" }),
  "maintenance-agent": feature("David, the maintenance coordinator", "maintenance", {
    follows: "maintenance",
    description:
      "David reads each work order in context, prices the fix, keeps the parts shop and readies the parts to buy. He changes a record only after saying what he will do.",
  }),
  "maintenance-web-search": feature("David's web search", "maintenance", {
    follows: "maintenance-agent",
    description:
      "David may search the public web for how a thing is fixed, what a part is called and where it is sold. Only the search words leave the building; a search naming a resident is refused.",
  }),
} as const satisfies Record<string, FeatureDefinition>;

export type FeatureKey = keyof typeof FEATURES;
export const FEATURE_KEYS = Object.keys(FEATURES) as FeatureKey[];

export function isFeatureKey(value: string): value is FeatureKey {
  return Object.hasOwn(FEATURES, value);
}
